



























































































import { Component, Prop, Vue } from 'vue-property-decorator';
import deck from '@/store/modules/deck';
import { getCollection } from '@/data/gt-library/registry';
import {
  collectSelectableNodes,
  getAllSelectableNodeIds,
  getDescendantIds,
} from '@/data/gt-library/treeUtils';
import { SelectableNode, TreeCollection } from '@/data/gt-library/types';

@Component({
  name: 'ContentTreePicker',
})
export default class ContentTreePicker extends Vue {
  @Prop({ required: true }) readonly collectionId!: string;
  @Prop({ default: undefined }) readonly selectedNodeIds!: string[] | undefined;
  @Prop({ default: undefined }) readonly startNodeId!: string | undefined;

  get collection(): TreeCollection | undefined {
    return getCollection(this.collectionId);
  }

  get supportsNextSteps(): boolean {
    return this.collectionId !== 'foretagspresentation-test';
  }

  get nodes(): SelectableNode[] {
    if (!this.collection) {
      return [];
    }
    return collectSelectableNodes(this.collection);
  }

  get effectiveStartId(): string {
    return this.startNodeId || this.collectionId;
  }

  get idSet(): Set<string> {
    if (!this.isFilterActive) {
      return new Set(this.nodes.map((n) => n.id));
    }
    return new Set(this.selectedNodeIds || []);
  }

  get isFilterActive(): boolean {
    return this.selectedNodeIds !== undefined;
  }

  get nextStepsEnabled(): boolean {
    return deck.Slides.some((slide) => slide.slideType === 'contentNextSteps');
  }

  set nextStepsEnabled(enabled: boolean) {
    void deck.setNextStepsEnabledAndSave(enabled);
  }

  rowIndent(depth: number): string {
    return String(depth * 1.25) + 'rem';
  }

  isStartNode(id: string): boolean {
    return this.effectiveStartId === id;
  }

  isChecked(node: SelectableNode): boolean {
    return this.idSet.has(node.id);
  }

  isIndeterminate(node: SelectableNode): boolean {
    if (node.kind !== 'nav' || !this.isFilterActive) {
      return false;
    }
    if (this.idSet.has(node.id)) {
      return false;
    }
    const descendants = getDescendantIds(this.collection!, node.id);
    const descendantOnly = descendants.filter((id) => id !== node.id);
    if (descendantOnly.length === 0) {
      return false;
    }
    const selectedCount = descendantOnly.filter((id) => this.idSet.has(id))
      .length;
    return selectedCount > 0 && selectedCount < descendantOnly.length;
  }

  ensureFilterIds(): string[] {
    if (!this.collection) {
      return [];
    }
    if (this.isFilterActive) {
      return (this.selectedNodeIds || []).slice();
    }
    return getAllSelectableNodeIds(this.collection);
  }

  emitSelection(ids: string[], nextStartNodeId: string) {
    this.$emit('update:selected-node-ids', ids);
    this.$emit('update:start-node-id', nextStartNodeId);
  }

  clearStartIfInvalid(ids: string[], currentStart?: string): string {
    const start = currentStart || this.collectionId;
    if (start === this.collectionId) {
      return this.collectionId;
    }
    if (ids.indexOf(start) >= 0) {
      return start;
    }
    return this.collectionId;
  }

  onCheckChange(node: SelectableNode, event: Event) {
    if (!this.collection) {
      return;
    }
    const target = event.target as HTMLInputElement;
    const checked = target.checked;
    let ids = this.ensureFilterIds();
    const descendants = getDescendantIds(this.collection, node.id);

    if (checked) {
      for (const descendantId of descendants) {
        if (ids.indexOf(descendantId) < 0) {
          ids.push(descendantId);
        }
      }
    } else {
      const removeSet = new Set(descendants);
      ids = ids.filter((id) => !removeSet.has(id));
    }

    const nextStart = this.clearStartIfInvalid(ids, this.startNodeId);
    this.emitSelection(ids, nextStart);
  }

  toggleRootStar() {
    this.$emit('update:start-node-id', this.collectionId);
  }

  toggleStar(node: SelectableNode) {
    if (!this.isChecked(node)) {
      return;
    }
    if (this.isStartNode(node.id)) {
      this.$emit('update:start-node-id', this.collectionId);
      return;
    }
    this.$emit('update:start-node-id', node.id);
  }

  resetAll() {
    this.$emit('update:selected-node-ids', undefined);
    this.$emit('update:start-node-id', this.collectionId);
  }
}
